import PropTypes from 'prop-types';
import { BsGeoAlt, BsTelephone, BsGlobe, BsListCheck, BsPhone } from 'react-icons/bs';
import { IoNavigate } from 'react-icons/io5';

import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import Select from '#globalComponents/Forms/Select';

export default function PlacesList({
	places,
	selectedPlaces,
	onPlaceClick,
	onPlaceSelectionToggle,
	onSelectAll,
	sortBy,
	onSortChange,
}) {
	const isPlaceSelected = (place) => {
		return selectedPlaces.some((p) => (p.placeId || p.place_id) === (place.placeId || place.place_id));
	};

	const isAllSelected = places.length > 0 && selectedPlaces.length === places.length;
	const isSomeSelected = selectedPlaces.length > 0 && !isAllSelected;

	if (places.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center h-full text-bgBase-800 px-4">
				<BsGeoAlt className="w-12 h-12 mb-3 text-bgBase-800" />
				<p className="text-md text-center">Keine Standorte gefunden</p>
				<p className="text-sm text-center mt-2">
					Wählen Sie eine Kategorie oder suchen Sie nach einem Unternehmen
				</p>
			</div>
		);
	}

	return (
		<div className="flex-1 flex flex-col min-h-0">
			{/* Header with Sort and Select All */}
			<div className="p-4 border-b space-y-3 bg-white sticky top-0 z-10">
				{/* Title and Select All Button */}
				<div className="flex items-center justify-between">
					<h2 className="text-lg font-semibold flex items-center gap-2">
						<BsListCheck className="h-5 w-5" />
						Unternehmen
					</h2>
					<button
						onClick={onSelectAll}
						className="h-9 px-3 text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50 rounded-md transition-colors"
					>
						{isAllSelected ? 'Keine' : isSomeSelected ? `Alle (${places.length})` : 'Alle'}
					</button>
				</div>

				{/* Sort Dropdown */}
				<Select value={sortBy} onChange={(e) => onSortChange(e.target.value)}>
					<option value="name">Nach Name</option>
					<option value="distance">Nach Entfernung</option>
					<option value="rating">Nach Bewertung</option>
				</Select>

				{/* Count */}
				<p className="text-sm text-text">
					<span className="font-semibold text-primary-600">{places.length}</span> Standorte gefunden
				</p>
			</div>

			{/* Scrollable List */}
			<div className="flex-1 overflow-y-auto">
				<div className="divide-y divide-gray-200">
					{places.map((place) => {
						const isChecked = isPlaceSelected(place);

						return (
							<div
								key={place.placeId || place.place_id}
								className={`p-3 transition-colors ${
									isChecked ? 'bg-primary-100' : 'bg-white hover:bg-primary-100'
								}`}
							>
								{/* Header with Name and Checkbox */}
								<div className="flex items-start justify-between mb-2">
									<h3 className="font-semibold text-sm line-clamp-2 flex-1 pr-2">{place.name}</h3>
									<Checkbox
										id={`place-${place.placeId || place.place_id}`}
										checked={isChecked}
										onChange={() => onPlaceSelectionToggle(place)}
										aria-label={`${place.name} auswählen`}
									/>
								</div>

								{/* Info Section */}
								<div className="space-y-1.5 text-xs text-text">
									{/* Category Badge */}
									{place.types && place.types[0] && (
										<div className="flex items-center gap-2 mb-2">
											<span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-text">
												{place.types[0].replace(/_/g, ' ')}
											</span>
										</div>
									)}

									{/* Phone */}
									{place.phone && (
										<div className="flex items-center gap-1.5">
											<BsTelephone className="h-3 w-3 flex-shrink-0 text-bgBase-800" />
											<a
												href={`tel:${place.phone}`}
												onClick={(e) => e.stopPropagation()}
												className="text-primary-600 hover:underline truncate"
											>
												{place.phone}
											</a>
										</div>
									)}

									{/* Address */}
									{place.address && (
										<div className="flex items-center gap-1.5">
											<BsGeoAlt className="h-3 w-3 flex-shrink-0 text-bgBase-800" />
											<span className="truncate">{place.address}</span>
										</div>
									)}

									{/* Website */}
									{place.website && (
										<div className="flex items-center gap-1.5">
											<BsGlobe className="h-3 w-3 flex-shrink-0 text-bgBase-800" />
											<a
												href={place.website}
												target="_blank"
												rel="noopener noreferrer"
												onClick={(e) => e.stopPropagation()}
												className="text-primary-600 hover:underline truncate"
											>
												{place.website}
											</a>
										</div>
									)}

									{/* Phone number */}
									{place.nationalPhoneNumber && (
										<div className="flex items-center gap-1.5">
											<BsPhone className="h-3 w-3 flex-shrink-0 text-bgBase-800" />
											<a
												href={`tel:${place.internationalPhoneNumber?.replace(/\s/g, '')}`}
												onClick={(e) => e.stopPropagation()}
												className="text-primary-600 hover:underline truncate"
											>
												{place.nationalPhoneNumber}
											</a>
										</div>
									)}
								</div>

								{/* Action Button */}
								<div className="mt-4">
									<Button
										onClick={() => onPlaceClick(place)}
										variant="solid"
										color="primary"
										className="w-full rounded-none"
									>
										<IoNavigate className="h-3 w-3" />
										Auf Karte zeigen
									</Button>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}

PlacesList.propTypes = {
	places: PropTypes.array.isRequired,
	selectedPlaces: PropTypes.array.isRequired,
	onPlaceClick: PropTypes.func.isRequired,
	onPlaceSelectionToggle: PropTypes.func.isRequired,
	onSelectAll: PropTypes.func.isRequired,
	sortBy: PropTypes.string.isRequired,
	onSortChange: PropTypes.func.isRequired,
};
